import { apiGet, apiPost } from './api.js';

/**
 * Staging/local tester wall. The token is per-device (localStorage) and
 * verified server-side on app boot. In production the API reports the gate
 * as disabled, so this whole module is a no-op there.
 */

const TOKEN_KEY = 'ig-access-gate-token';

export function getGateToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setGateToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage unavailable — the gate simply reappears next visit */
  }
}

export function clearGateToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolves the gate state for this device: 'open' when the gate is disabled
 * (production) or this device holds a valid token; 'locked' otherwise.
 * Fails OPEN on network/API errors — a broken gate must never brick the app.
 */
export async function resolveGateState(): Promise<'open' | 'locked'> {
  try {
    const status = await apiGet<{ enabled: boolean }>('/access-gate/status', { suppressErrorToast: true });
    if (!status?.enabled) return 'open';
    const token = getGateToken();
    if (!token) return 'locked';
    const check = await apiPost<{ valid: boolean }>(
      '/access-gate/verify',
      { token },
      { suppressErrorToast: true },
    );
    if (check?.valid) return 'open';
    clearGateToken();
    return 'locked';
  } catch {
    return 'open';
  }
}
